// orgSwitchUrl.js — the workspace records an ORG SWITCH must treat as "stale
// URL segments", for the shell's `sdk.orgSwitched` handler.
//
// WHY (WS-ORG-SWITCH-URL-DOUBLE-WORKSPACE-SEGMENT-1, measured live
// 2026-08-21): scheme A stamps EVERY workspace URL as
// `/w/<org>/<ws-handle>/<route…>`. On an org switch the address bar still
// holds the OUTGOING org's URL, so segment 2 is the OUTGOING workspace's
// handle. `syncWorkspaceUrl(..., {rewriteOrg:true})` rebuilds the path from
// the settled state, and its belt-and-suspenders strip drops segment 2 only
// when it RECOGNISES it — against `state.workspace` + `state.workspaces`, or
// the raw `ws_<nanoid>` id shape.
//
// The shell was passing the INCOMING org's list. A raw-id handle
// (`ws_UYAOhQyNjm`) still self-identifies by shape, but a SLUG handle does
// not: leaving `/w/system/hq/tasks` for Bellforge produced
// `/w/bellforge/ws_UYAOhQyNjm/hq/tasks` — the outgoing `hq` slug kept as an
// extra segment, because no bellforge workspace is named `hq`.
//
// The handler holds the outgoing scope at entry (before the refetch pipeline
// re-scopes `root.workspace` / `root.workspaces`), so the fix is to CARRY it,
// not to guess: a slug is only strippable when it is a workspace we know we
// just left. Nothing here widens the strip to unknown segments — a real route
// segment (`/w/system/tasks`, the legacy bare form) is still never eaten.
//
// FA204 / frank trap: the export below is self-contained — no module-scope
// reads, no bare sibling-export calls (workspacePrefix.js header, readerVoice
// precedent).

// Merge the incoming org's workspaces with the outgoing org's workspaces and
// the outgoing ACTIVE workspace record, deduped by identity. Non-object and
// unidentifiable entries are dropped; every input is optional.
export const orgSwitchStaleWorkspaces = (outgoingWorkspace, outgoingList, incomingList) => {
  const out = []
  const seen = new Set()
  const push = (w) => {
    if (!w || typeof w !== 'object') return
    const sig = String(w.id ?? w._id ?? w.key ?? w.slug ?? '')
    if (!sig || seen.has(sig)) return
    seen.add(sig)
    out.push(w)
  }
  if (Array.isArray(incomingList)) for (const w of incomingList) push(w)
  if (Array.isArray(outgoingList)) for (const w of outgoingList) push(w)
  push(outgoingWorkspace)
  return out
}

// The workspace record an org switch may stamp into the URL, or `null`.
//
// WHY (WS-ORG-SWITCH-URL-SYNC-LANDS-LATE-1, measured live 2026-08-21): the
// shell used to read `s.root.workspace` AFTER awaiting the whole
// `onWorkspaceChange` refetch pipeline and gate the URL sync on that record's
// org identity. Two failures came out of one root cause — the URL target was
// derived from a record the pipeline had not settled yet:
//   • the record was still the OUTGOING workspace (old org id) → the guard
//     failed and the sync was SKIPPED for that pass, so the address bar kept
//     naming the OLD org for ~9 s, until an unrelated later sync healed it;
//   • the record was a THIN `{ key }` row (no `id`, no `org_id`) → no org
//     evidence at all, and a 7 s observation window saw no rewrite.
// During that window the URL is a cross-org lie: a reload or a copied link
// resolves through `reconcileMwtOrgFromUrl` back to the org the user left.
//
// The fix is to sync from the switch's OWN resolved identity the moment it
// exists (the pipeline calls back at workspace-record adoption — one fetch,
// not the whole pipeline) and to keep the late pass as a corrective no-op.
// This validator is the shared decision both passes use:
//   • a record carrying a DIFFERENT org id is refused (an outgoing/superseded
//     record must never be stamped for the new org);
//   • a record with NO org evidence (thin `{ key }`) is ACCEPTED — the caller
//     vouches for it as this switch's target, and its handle is all the URL
//     needs;
//   • a record with no stampable handle at all is refused (syncWorkspaceUrl
//     would drop the workspace segment).
//
// FA204 / frank trap: self-contained — no module-scope reads, no bare
// sibling-export calls (see this file's header).
export const orgSwitchUrlWorkspace = (candidate, newOrgId) => {
  if (!candidate || typeof candidate !== 'object') return null
  const wsOrg = String(
    candidate.org_id ??
      candidate.organization ??
      candidate.organizationId ??
      candidate.org ??
      ''
  )
  const want = String(newOrgId ?? '')
  if (wsOrg && want && wsOrg !== want) return null
  const handle = candidate.slug ?? candidate.key ?? candidate.id ?? candidate._id
  if (!handle) return null
  return candidate
}

// The workspace record a switch's OWN workspace hop names, resolved against
// the record pools the shell already holds, or `null`.
//
// The real org switcher hands `sdk.switchOrg` a `homeWorkspaceId`, and that hop
// completes BEFORE `sdk.orgSwitched` is emitted — so the switch knows its
// target workspace with no fetch, and the address bar can be truthful in the
// SAME tick as the shell re-scope instead of waiting on the workspace resolve
// (WS-ORG-SWITCH-URL-SYNC-LANDS-LATE-1).
//
// The hop carries a Mongo id, which is NOT a URL handle: `workspaceUrlHandle`
// falls back to `id` last, so stamping `{ id: <mongo-hex> }` would put a raw
// hex segment in the path that no resolver claims and no stale-segment strip
// recognises. So resolve it to a real row first (the switcher's own
// `orgWorkspaces` pool is populated for exactly the org it just drilled into),
// and accept the id alone ONLY when it is already `ws_`-shaped — the one id
// form that IS a first-class handle.
//
// `pools` is a list of arrays; any may be undefined. No mutation, no side
// effects. FA204 / frank trap: self-contained (see this file's header).
export const orgSwitchHopWorkspace = (hopWorkspaceId, pools) => {
  const want = String(hopWorkspaceId ?? '')
  if (!want) return null
  const lists = Array.isArray(pools) ? pools : []
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const w of list) {
      if (!w || typeof w !== 'object') continue
      if (String(w.id ?? '') === want || String(w._id ?? '') === want) return w
      if (String(w.key ?? '') === want || String(w.slug ?? '') === want) return w
    }
  }
  return /^ws_[A-Za-z0-9_-]+$/.test(want) ? { key: want } : null
}
